// Caja de compra y barra fija de compra en móvil.
// Componente pdp-*: el HTML llega renderizado desde Liquid y este módulo solo añade interacción.

// The cart module is loaded only when adding to cart: if it fails to load, prices and
// options keep working instead of the whole buy box going dead. Liquid passes its versioned
// URL: a bare './pdp-cart.js' is served stale by the CDN after a theme change.
const loadCart = (url) => import(url || './pdp-cart.js');

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

/* ---------- Caja de compra ---------- */
class PdpBuyBox extends HTMLElement {
  connectedCallback() {
    const dataElement = this.querySelector('[data-pdp-data]');
    this.form = this.querySelector('form');
    if (!dataElement || !this.form) return;
    this.data = JSON.parse(dataElement.textContent);
    this.variantInput = this.querySelector('[data-variant-input]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.buyNow = this.querySelector('[data-buy-now]');
    this.subscribe = this.querySelector('[data-subscribe]');
    this.error = this.querySelector('[data-error]');
    this.freeShipping = this.querySelector('[data-free-shipping-message]');
    this.addons = this.querySelector('[data-addons]');

    // Listen on the whole box in the capture phase: theme or app scripts that stop the
    // event inside the product form can no longer keep prices from updating.
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-quantity], [data-plan], [data-variant-input], [data-addon]')) this.update();
    }, true);
    this.form.addEventListener('submit', (event) => {
      if (!this.addButton || this.plan() === 'subscription') return;
      event.preventDefault();
      loadCart(this.dataset.cartModule)
        .then(({ addToCart }) => addToCart(this.form, this.addButton, this.error, this.addonIds()))
        .catch(() => this.form.submit());
    });
    this.buyNow?.addEventListener('click', (event) => {
      if (this.buyNow.getAttribute('aria-disabled') === 'true') event.preventDefault();
    });
    this.update();
  }

  plan() {
    return this.querySelector('[data-plan]:checked')?.value || 'once';
  }

  // Add-ons are offered for one-time purchases only: subscriptions check out on Treli.
  addonIds() {
    if (!this.addons || this.addons.hidden || this.plan() === 'subscription') return [];
    return [...this.addons.querySelectorAll('[data-addon]:checked')].map((input) => input.value);
  }

  // Buy-now total with the checked add-ons. Liquid formats every other price; this one is the
  // only sum done here, so it copies the store format ($ and dots, no decimals).
  checkoutPrice(option, isSubscription) {
    const addons = isSubscription || !this.addons ? []
      : [...this.addons.querySelectorAll('[data-addon]:checked')];
    if (!addons.length) return isSubscription ? option.sub : option.once;
    const cents = addons.reduce((sum, input) => sum + Number(input.dataset.price || 0), option.onceCents);
    return `$${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(Math.round(cents / 100))}`;
  }

  quantity() {
    return Number(this.querySelector('[data-quantity]:checked')?.value || 1);
  }

  setField(name, value, template) {
    this.querySelectorAll(`[data-field="${name}"]`).forEach((element) => {
      element.hidden = value == null;
      if (value == null) return;
      const pattern = template || element.dataset.template;
      const text = pattern ? this.dataset[`${pattern}Template`].replace('[monto]', value) : value;
      element.textContent = text;
    });
  }

  update() {
    const variantId = this.variantInput.value;
    const variant = this.data.variants[variantId];
    if (!variant) return;
    const quantity = this.quantity();
    const option = variant.options[quantity] || variant.options[1];
    const isSubscription = this.plan() === 'subscription';
    const current = isSubscription ? option.sub : option.once;

    this.quantityInput.value = quantity;
    this.setField('once', option.once);
    this.setField('onceList', option.onceList);
    this.setField('onceSave', option.onceSave);
    this.setField('onceUnit', option.onceUnit);
    this.setField('sub', option.sub);
    this.setField('subList', option.subList);
    this.setField('subSave', option.subSave);
    this.setField('subUnit', option.subUnit);
    this.setField('subPercent', option.subPercent);
    this.setField('current', current);
    this.setField('checkout', this.checkoutPrice(option, isSubscription));
    this.setField('currentList', isSubscription ? option.subList : option.onceList);

    // Free shipping applies to one-time purchases only: subscriptions check out on Treli.
    if (this.freeShipping) {
      const threshold = Number(this.dataset.freeShippingFrom || 0);
      this.freeShipping.hidden = !(threshold > 0 && !isSubscription && option.onceCents >= threshold);
    }

    if (this.addButton) {
      this.addButton.hidden = isSubscription;
      this.addButton.disabled = !variant.available;
      this.addButton.querySelector('[data-add-label]').textContent = variant.available
        ? this.dataset.addLabel
        : this.dataset.soldOut;
    }
    // With data-addons-single the store only discounts the add-on when a single unit is bought,
    // so the offer is hidden for larger quantities instead of showing a price the cart will not honor.
    if (this.addons) {
      this.addons.hidden = isSubscription || (this.hasAttribute('data-addons-single') && quantity > 1);
    }
    if (this.buyNow) {
      this.buyNow.hidden = isSubscription;
      if (variant.available) {
        const addons = this.addonIds().map((id) => `,${id}:1`).join('');
        this.buyNow.href = `${window.Shopify?.routes?.root || '/'}cart/${variantId}:${quantity}${addons}`;
        this.buyNow.removeAttribute('aria-disabled');
      } else {
        this.buyNow.removeAttribute('href');
        this.buyNow.setAttribute('aria-disabled', 'true');
      }
    }
    if (this.subscribe && this.data.subscriptionUrl) {
      this.subscribe.hidden = !isSubscription;
      const url = new URL(this.data.subscriptionUrl);
      if (quantity > 1) url.searchParams.set('quantity', quantity);
      this.subscribe.href = url.toString();
    }

    const unit = quantity === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
    const label = this.dataset.quantityTemplate
      .replace('[cantidad]', `${quantity} ${unit}`)
      .replace('[forma]', isSubscription ? this.dataset.subLabel : this.dataset.onceLabel);
    this.dispatchEvent(
      new CustomEvent('pdp:buy-box:change', {
        bubbles: true,
        detail: {
          price: current,
          label,
          quantityLabel: `${quantity} ${unit}`,
          planLabel: isSubscription ? this.dataset.subLabel : this.dataset.onceLabel,
        },
      })
    );
  }
}
define('pdp-buy-box', PdpBuyBox);

/* ---------- Barra fija de compra en móvil ---------- */
class PdpStickyBar extends HTMLElement {
  connectedCallback() {
    this.buyBox = document.querySelector('pdp-buy-box');
    if (!this.buyBox) return;
    this.price = this.querySelector('[data-sticky-price]');
    this.label = this.querySelector('[data-sticky-label]');

    this.quantity = this.querySelector('[data-sticky-quantity]');

    // With a quantity slot, the quantity goes next to the price and the plan gets its own line.
    this.onChange = (event) => {
      const { price, label, quantityLabel, planLabel } = event.detail;
      this.price.textContent = price;
      if (this.quantity && quantityLabel) {
        this.quantity.textContent = `· ${quantityLabel}`;
        this.label.textContent = planLabel || label;
      } else {
        this.label.textContent = label;
      }
    };
    document.addEventListener('pdp:buy-box:change', this.onChange);
    // The buy box announced its first state before this bar was listening: ask again.
    this.buyBox.update?.();

    const always = this.hasAttribute('data-always');
    this.observer = new IntersectionObserver(([entry]) => {
      this.hidden = always ? entry.isIntersecting : entry.isIntersecting || entry.boundingClientRect.top > 0;
    });
    this.observer.observe(this.buyBox);

    this.querySelector('[data-sticky-action]').addEventListener('click', () => {
      this.buyBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.buyBox.querySelector('input:not([type="hidden"]), select, button, a')?.focus({ preventScroll: true });
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('pdp:buy-box:change', this.onChange);
  }
}
define('pdp-sticky-bar', PdpStickyBar);
